import { Box, TextField, Checkbox, styled, Autocomplete } from "@mui/material";
import FormControlLabel from '@mui/material/FormControlLabel';
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { countries } from "../../../../global";
import AuditInfoLabel from "../../../../components/AuditInfoLabel";

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  marginBottom: "20px",
  alignItems: "top",
  gap: "30px",
  flexDirection: "row",
  justifyContent: "normal!important"
}));

const StyledTextField = styled(TextField)(() => ({
  width: "30rem"
}));

const SiteForm = ({ 
  siteData, 
  formikRef, 
  invalidSiteNames=[], 
  invalidSiteAbbreviations=[],
  invalidSingleAbvs=[],
  canEdit=false,
  isAddSite=false,
  onDeactivateSite}) => {

  const initialValues = {
    siteName: siteData?.siteName ?? '',
    abbreviation: siteData?.abbreviation ?? '',
    singleLetterAbv: siteData?.singleLetterAbv ?? '',
    isActive: siteData?.isActive ?? false,
    isExternalSite: siteData?.isExternalSite ?? false,
    siteContact: siteData?.siteContact ?? '',
    address: siteData?.address ?? '',
    city: siteData?.city ?? '',
    state: siteData?.state ?? '',
    postalCode: siteData?.postalCode ?? '',
    country: siteData?.country ?? '',
    phoneNumber: siteData?.phoneNumber ?? '',
    createdByEmail: siteData?.createdByEmail,
    createdDate: siteData?.createdDate,
    lastModifiedByEmail: siteData?.lastModifiedByEmail,
    lastModifiedDate: siteData?.lastModifiedDate
  };

  const validationSchema = Yup.object({
    siteName: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Site Name is required')
      .max(50, 'Site Name must be max 50 characters')
      .test("unique-name", "Site Name already exists", (value) => {
        if (!value) return true;
        const lowerCaseValue = value.toLowerCase().trim();
        return !invalidSiteNames.some((name) => name?.toLowerCase().trim() === lowerCaseValue);
      }),
    abbreviation: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .min(3, 'Must be 3 characters')
      .max(3, 'Must be 3 characters')
      .test("unique-name", "Already exists", (value) => {
        if (!value) return true;
        const lowerCaseValue = value.toLowerCase().trim();
        return !invalidSiteAbbreviations.some((a) => a?.toLowerCase().trim() === lowerCaseValue);
      }),
    singleLetterAbv: Yup.string()
      .max(1, 'Must be 1 character')
      .test("unique-name", "Already exists", (value) => {
        if (!value) return true;
        const lowerCaseValue = value.toLowerCase().trim();
        return !invalidSingleAbvs.some((x) => x?.toLowerCase().trim() === lowerCaseValue);
      }),
    siteContact: Yup.string()
      .max(250, 'Site Contact must be max 250 characters'),
    city: Yup.string()
      .max(100, 'City must be max 100 characters'),
    state: Yup.string()
      .max(20, 'State must be max 20 characters'),
    postalCode: Yup.string()
      .max(20, 'Postal Code must be max 20 characters'),
    phoneNumber: Yup.string()
      .max(20, 'Phone Number must be max 20 characters'), 
  });

  return (
    <Formik
      initialValues={initialValues}
      innerRef={formikRef}
      enableReinitialize={true}
      validationSchema={validationSchema}
    >
      {({ values, handleChange, handleBlur, setFieldValue, errors, touched, isValid, dirty }) => (
        <Form style={{margin:"10px"}}>

          <StyledBox sx={{ justifyContent: "space-between" }}>

            <StyledTextField
              name="siteName"
              label="Site name"
              variant="outlined" size="small" margin="none" 
              value={values.siteName}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={!isAddSite || !canEdit}
              error={touched.siteName && Boolean(errors.siteName)}
              helperText={touched.siteName && errors.siteName}
            />

            <TextField style={{ width: "6rem" }}
              name="abbreviation"
              label="Abbreviation"
              variant="outlined" size="small" margin="none"
              value={values.abbreviation} 
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{ maxLength: 3 }} 
              disabled={!canEdit}
              error={touched.abbreviation && Boolean(errors.abbreviation)}
              helperText={touched.abbreviation && errors.abbreviation}
            />

            <TextField style={{ width: "6rem" }}
              name="singleLetterAbv"
              label="Letter Abbr."
              variant="outlined" size="small" margin="none"
              value={values.singleLetterAbv} 
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{ maxLength: 1 }} 
              disabled={!canEdit}
              error={touched.singleLetterAbv && Boolean(errors.singleLetterAbv)}
              helperText={touched.singleLetterAbv && errors.singleLetterAbv}
            />

            <Box style={{ justifyContent: "space-between" }}>
              <FormControlLabel
                label="Active"
                control={
                  <Checkbox
                    onChange={(e) => {
                      setFieldValue('isActive', e.target.checked);
                      onDeactivateSite?.(e.target.checked);
                    }}
                    name="isActive"
                    checked={values.isActive}
                    disabled={!canEdit} 
                  />}
              />

              <FormControlLabel
                label="External Site"
                style={{marginLeft:"10px"}}
                control={
                  <Checkbox
                    onChange={handleChange}
                    name="isExternalSite"
                    checked={values.isExternalSite}
                    disabled={!canEdit}  
                  />}
              />
            </Box>
            
          </StyledBox>

          <StyledBox sx={{ justifyContent: "space-between" }}>

            <StyledTextField
              name="siteContact"
              label="Site Contact"
              variant="outlined" size="small" margin="none" 
              value={values.siteContact}
              onChange={handleChange}
              onBlur={handleBlur}  
              disabled={!canEdit}
              error={touched.siteContact && Boolean(errors.siteContact)}
              helperText={touched.siteContact && errors.siteContact}
            />

            <StyledTextField
              name="address"
              label="Address"
              variant="outlined" size="small" margin="none" 
              value={values.address}
              onChange={handleChange}
              onBlur={handleBlur}  
              disabled={!canEdit}
              error={touched.address && Boolean(errors.address)}
              helperText={touched.address && errors.address}
            />

          </StyledBox>

          <StyledBox sx={{ justifyContent: "space-between" }}>

            <StyledTextField
              name="city"
              label="City"
              variant="outlined" size="small" margin="none" 
              value={values.city}
              onChange={handleChange}
              onBlur={handleBlur}  
              disabled={!canEdit}
              error={touched.city && Boolean(errors.city)}
              helperText={touched.city && errors.city}
            />

            <TextField
              style={{ width: "14rem" }}
              name="state"
              label="State"
              variant="outlined" size="small" margin="none" 
              value={values.state}
              onChange={handleChange}
              onBlur={handleBlur}  
              disabled={!canEdit}
              error={touched.state && Boolean(errors.state)}
              helperText={touched.state && errors.state}
            />

            <TextField
              style={{ width: "14rem" }}
              name="postalCode"
              label="Postal Code"
              variant="outlined" size="small" margin="none" 
              value={values.postalCode}
              onChange={handleChange}
              onBlur={handleBlur}  
              disabled={!canEdit}
              error={touched.postalCode && Boolean(errors.postalCode)}
              helperText={touched.postalCode && errors.postalCode}
            />

          </StyledBox>

          <StyledBox sx={{ justifyContent: "space-between" }}>

            <Autocomplete
              noOptionsText={"No Country Found"}
              value={values.country}
              onChange={(e, val) => setFieldValue('country', val)}
              disablePortal
              disabled={!canEdit}
              options={countries.map(c => c)}
              sx={{ width: "30rem" }}
              renderInput={(params) => <TextField 
                {...params} 
                name="country"  variant="outlined" size="small" margin="none" 
                label="Country"
              />}
              autoSelect                  
            />

            <StyledTextField
              name="phoneNumber"
              label="Phone Number"
              variant="outlined" size="small" margin="none" 
              value={values.phoneNumber}
              onChange={handleChange}
              onBlur={handleBlur}  
              disabled={!canEdit}
              error={touched.phoneNumber && Boolean(errors.phoneNumber)}
              helperText={touched.phoneNumber && errors.phoneNumber}
            />

          </StyledBox>

          <AuditInfoLabel auditObject={values} />

        </Form>
      )}
    </Formik>
  );
};

export default SiteForm;
